<script lang="ts">
	import * as DropdownMenu from '$lib/components/ui/dropdown-menu/index.js';
	import SunIcon from '@lucide/svelte/icons/sun';
	import MoonIcon from '@lucide/svelte/icons/moon';
	import RotateCcwIcon from '@lucide/svelte/icons/rotate-ccw';
	import LogOutIcon from '@lucide/svelte/icons/log-out';
	import KeyboardIcon from '@lucide/svelte/icons/keyboard';
	import { isDark, toggleTheme } from '$lib/theme.svelte';

	// Open WebUI's bottom user row (`UserMenu`): a rounded-xl row with a 22px
	// avatar carrying a green status dot and the user's name, opening a menu
	// of the account-level actions. In the collapsed rail only the avatar shows.
	let {
		label,
		isAdmin = false,
		onResetOnboarding,
		onSignOut,
		onShortcuts,
	}: {
		label: string;
		isAdmin?: boolean;
		onResetOnboarding?: () => void;
		onSignOut: () => void;
		onShortcuts?: () => void;
	} = $props();

	const initial = $derived((label.trim()[0] ?? '?').toUpperCase());
</script>

{#snippet avatar(size: string)}
	<span class="relative shrink-0 self-center">
		<span
			class="flex {size} items-center justify-center rounded-full bg-black text-[0.625rem] font-medium text-white dark:bg-white dark:text-black"
			aria-hidden="true">{initial}</span
		>
		<span class="absolute -right-0.5 -bottom-0.5 flex size-2.5">
			<span
				class="relative inline-flex size-2.5 rounded-full border-2 border-white bg-green-500 dark:border-gray-900"
				aria-hidden="true"
			></span>
		</span>
	</span>
{/snippet}

<DropdownMenu.Root>
	<DropdownMenu.Trigger
		class="flex w-full items-center rounded-xl px-1.5 py-1.5 text-left transition hover:bg-gray-100 dark:hover:bg-gray-900 group-data-[collapsible=icon]:size-8.5 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-0"
		aria-label="User menu — {label}"
		title={label}
		data-testid="sidebar-user-menu"
	>
		<span class="mr-3 group-data-[collapsible=icon]:mr-0">
			{@render avatar('size-5.5')}
		</span>
		<span class="min-w-0 flex-1 truncate font-normal group-data-[collapsible=icon]:hidden">{label}</span>
	</DropdownMenu.Trigger>
	<DropdownMenu.Content side="top" align="start" sideOffset={6} class="w-[calc(var(--sidebar-width)-1rem)] min-w-[12rem] font-sans">
		<div class="flex items-center gap-2 px-2 py-1.5 text-xs">
			{@render avatar('size-4.5')}
			<span class="min-w-0 flex-1 truncate">{label}</span>
		</div>
		<DropdownMenu.Separator />
		<DropdownMenu.Item onSelect={toggleTheme}>
			{#if isDark()}
				<SunIcon aria-hidden="true" />
				Light mode
			{:else}
				<MoonIcon aria-hidden="true" />
				Dark mode
			{/if}
		</DropdownMenu.Item>
		{#if onShortcuts}
			<DropdownMenu.Item onSelect={onShortcuts}>
				<KeyboardIcon aria-hidden="true" />
				Keyboard shortcuts
			</DropdownMenu.Item>
		{/if}
		{#if isAdmin && onResetOnboarding}
			<DropdownMenu.Separator />
			<DropdownMenu.Item onSelect={onResetOnboarding}>
				<RotateCcwIcon aria-hidden="true" />
				Reset onboarding
			</DropdownMenu.Item>
		{/if}
		<DropdownMenu.Separator />
		<DropdownMenu.Item onSelect={onSignOut}>
			<LogOutIcon aria-hidden="true" />
			Sign out
		</DropdownMenu.Item>
	</DropdownMenu.Content>
</DropdownMenu.Root>
